import { LockstepClient, type ServerMsg } from '@somnhold/protocol';
import { reloadIfStale } from '../update.ts';

/**
 * Where the link stands. The last four end it for good: the room was full, called off, opened again
 * elsewhere (another tab or device), or is gone (over, or lost with a restart).
 */
export type LinkStatus = 'connecting' | 'open' | 'lost' | 'refused' | 'full' | 'cancelled' | 'replaced' | 'gone';

/**
 * Nothing from the room for this long and the connection counts as dead, even if the browser says it is
 * open: the room sends a tick every 83 ms and a pong every second. A phone that locked or switched networks
 * often keeps a socket that looks open and carries nothing, with no close ever coming.
 */
const SILENT_MS = 4000;
/** Reconnects refused this many times in a row while the network works: the room is gone. */
const GONE_AFTER = 3;
/** Samples kept for the device report (about 20 minutes of ticks). */
const KEEP = 15000;
/** A wait between ticks worth keeping with its context for the device report. */
const LATE_GAP_MS = 1000;

/** One of the longest waits between ticks: how long, at which tick, ms after the last whole state (-1: none yet), and whether the page was hidden. */
export interface WorstGap {
  ms: number;
  tick: number;
  whole: number;
  hidden: boolean;
}

/** Keeps the three longest. */
const keepWorst = (list: WorstGap[], g: WorstGap): void => {
  list.push(g);
  list.sort((a, b) => b.ms - a.ms);
  if (list.length > 3) list.length = 3;
};

/**
 * WebSocket transport for a live room. Reconnects on its own (at once when the connection went silent or
 * the page came back, with backoff after a failure); every reconnect joins again and the server answers
 * with a fresh snapshot (resync), so a dropped player comes back seamlessly.
 */
export class LiveLink {
  readonly client: LockstepClient;
  private ws: WebSocket | null = null;
  private closed = false;
  private retry = 0;
  private refusals = 0;
  private pinger = 0;
  private retryTimer = 0;
  private lastHeard = 0;
  private everOpen = false;
  /** Round trip to the room in ms, from the last pong (0 until one comes back). */
  rtt = 0;
  /** Frames per second on screen, set by the battle screen (sent along with the ping, for the room's lag log). */
  fps = 0;
  /** The longest frame since the last ping, in ms, raised by the battle screen (a frozen page, for the lag log). */
  jank = 0;
  /** The longest wait between two ticks from the room since the last ping, in ms (the network, for the lag log). */
  private gapMax = 0;
  private lastTick = 0;
  /** For the screen's device report (PerfMeter): every wait between ticks, every round trip, every reconnect. */
  readonly seen = { gaps: [] as number[], rtts: [] as number[], reconnects: 0, ext: '', big: 0, worst: [] as WorstGap[] };
  /** When the last whole state (welcome, resync) came: a long wait right after one points at it, not at the network. */
  private lastWhole = 0;
  /** Time spent on the room's messages (each tick simulated here, and the screen's answer to it), ms: the screen takes it each drawing. */
  busy = 0;
  onStatus: (s: LinkStatus) => void = () => undefined;

  constructor(
    private campaignId: string,
    /** A sector to defend, -1 for the crypt, or a raw query ("incursion=3", "room=inc-u-ana"). */
    private sector: number | string,
    pid: string,
  ) {
    this.client = new LockstepClient(pid, (m) => {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
    });
  }

  /** Resolves once the first snapshot arrives (never, when the page is from before a deploy: it reloads). */
  async connect(): Promise<void> {
    if (await reloadIfStale()) return new Promise(() => undefined);
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (err?: Error): void => {
        if (settled) return;
        settled = true;
        if (err) reject(err);
        else resolve();
      };
      // For good: the screen is told, the link stops, a pending connect() fails with the reason.
      const end = (status: LinkStatus): void => {
        this.onStatus(status);
        this.close();
        settle(new Error(status));
      };
      const later = (): void => {
        this.onStatus('lost');
        this.retry++;
        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(open, Math.min(8000, 400 * 2 ** this.retry));
      };
      const open = (): void => {
        if (this.closed) return;
        window.clearTimeout(this.retryTimer);
        this.onStatus('connecting');
        const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
        // Sector -1 is the crypt.
        const q = typeof this.sector === 'string' ? this.sector : this.sector < 0 ? 'dungeon=1' : `sector=${this.sector}`;
        const ws = new WebSocket(`${proto}//${location.host}/api/campaigns/${this.campaignId}/live?${q}`);
        this.ws = ws;
        let opened = false;
        // Events of a socket already given up on (replaced by a newer one) are ignored.
        ws.addEventListener('open', () => {
          if (ws !== this.ws) return;
          opened = true;
          // A reconnect after a deploy: a page of the old build would desync against the room every few
          // seconds, so it reloads instead (the bastion then offers the way back in).
          if (this.everOpen) {
            void reloadIfStale(true);
            this.seen.reconnects++;
          }
          this.lastTick = 0;
          this.seen.ext = ws.extensions;
          this.everOpen = true;
          this.retry = 0;
          this.refusals = 0;
          this.lastHeard = Date.now();
          this.onStatus('open');
          this.client.join(typeof this.sector === 'number' ? this.sector : -1);
        });
        ws.addEventListener('message', (ev) => {
          if (ws !== this.ws) return;
          this.lastHeard = Date.now();
          let m: ServerMsg;
          const text = String(ev.data);
          if (text.length > this.seen.big) this.seen.big = text.length;
          try {
            m = JSON.parse(text) as ServerMsg;
          } catch {
            return;
          }
          if (m.t === 'pong') {
            this.rtt = Date.now() - m.at;
            if (this.seen.rtts.length < KEEP) this.seen.rtts.push(Math.min(this.rtt, 60_000));
          }
          if (m.t === 'tick') {
            const now = performance.now();
            if (this.lastTick) {
              // A locked phone's socket can sit silent for minutes: capped, so one stall doesn't drown the report.
              const gap = Math.min(now - this.lastTick, 60_000);
              this.gapMax = Math.max(this.gapMax, gap);
              if (this.seen.gaps.length < KEEP) this.seen.gaps.push(gap);
              if (gap > LATE_GAP_MS) keepWorst(this.seen.worst, { ms: Math.round(gap), tick: m.tick, whole: this.lastWhole ? Math.round(now - this.lastWhole) : -1, hidden: document.visibilityState !== 'visible' });
            }
            this.lastTick = now;
          }
          if (m.t === 'full' || m.t === 'cancelled' || m.t === 'replaced') return end(m.t);
          const t0 = performance.now();
          this.client.onMessage(m);
          this.busy += performance.now() - t0;
          if (m.t === 'welcome' || m.t === 'resync') {
            this.lastWhole = performance.now();
            settle();
          }
        });
        ws.addEventListener('close', (ev) => {
          if (ws !== this.ws || this.closed) return;
          this.ws = null;
          if (!settled && !opened && ev.code !== 1000 && this.retry > 3) {
            this.onStatus('refused');
            this.close();
            settle(new Error('refused'));
            return;
          }
          // Turned away while the network works (the room answered no): after a few, the room is gone,
          // instead of "reconnecting" forever over a frozen board.
          if (!opened && this.everOpen) {
            void networkUp().then((up) => {
              if (this.closed) return;
              if (up && ++this.refusals >= GONE_AFTER) end('gone');
              else later();
            });
            return;
          }
          later();
        });
      };
      // Drop a connection that went silent (or whatever is left of it) and connect again right away.
      const renew = (): void => {
        if (this.closed) return;
        const old = this.ws;
        this.ws = null;
        try {
          old?.close();
        } catch {
          /* already gone */
        }
        this.retry = 0;
        open();
      };
      const silent = (): boolean => this.lastHeard > 0 && Date.now() - this.lastHeard > SILENT_MS;
      this.watch = (): void => {
        if (this.closed) return;
        if (this.ws?.readyState === WebSocket.OPEN && silent()) renew();
        else if (!this.ws) renew();
      };
      open();
      this.pinger = window.setInterval(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        if (silent()) return renew();
        this.ws.send(JSON.stringify({ t: 'ping', at: Date.now(), ...(this.rtt > 0 ? { rtt: this.rtt, fps: Math.round(this.fps), jank: Math.round(this.jank), gap: Math.round(this.gapMax) } : {}) }));
        this.jank = 0;
        this.gapMax = 0;
      }, 1000);
      // Back from the background or on a network again: check at once instead of waiting for the next ping.
      document.addEventListener('visibilitychange', this.onVisible);
      window.addEventListener('online', this.watch);
    });
  }

  /** Set by connect(): renew the connection if it went silent, or reconnect now if there is none. */
  private watch: () => void = () => undefined;
  private readonly onVisible = (): void => {
    if (document.visibilityState === 'visible') this.watch();
  };

  close(): void {
    this.closed = true;
    window.clearInterval(this.pinger);
    window.clearTimeout(this.retryTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.watch);
    try {
      this.ws?.send(JSON.stringify({ t: 'leave' }));
    } catch {
      /* not open */
    }
    this.ws?.close(1000);
  }
}

/** Whether the server answers at all (so a refused reconnect means the room, not the network). */
const networkUp = async (): Promise<boolean> => {
  if (!navigator.onLine) return false;
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', signal: AbortSignal.timeout(3000) });
    return res.ok || res.status === 404;
  } catch {
    return false;
  }
};
